import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Alert, Button, QuantityStepper, RadioGroup, useCart } from '@alwindows/shared'
import { auspostCalc, type AuspostService } from '@alwindows/shared/api/auspost'
import {
  getCollectOptions,
  getShippingOptions,
  type CollectOption,
  type ShippingOption,
} from '@alwindows/shared/api/spareParts'
import { QTY_UPPER_LIMIT } from '../catalog/constants'
import { useShipment } from '../shipment/ShipmentContext'

// Depot postcodes, ported from the legacy app. The AusPost rate is
// calculated from the depot closest to the destination so the customer sees
// a realistic price.
const VIC_FROM_POSTCODE = '3175'
const QLD_FROM_POSTCODE = '4207'

// Legacy copy preserved verbatim — see al_spare_parts/src/components/Pages/Cart/Body.js.
const DELIVERY_DISABLED_PAINT =
  "Paints and sprays can't be delivered."
const DELIVERY_DISABLED_WEIGHT =
  'Total weight of items exceeds maximum for delivery, please select a pick up location.'

/**
 * Which depot to ship from, given a destination postcode.
 *
 * QLD (4000-4999) ships from the QLD depot. All other destinations ship from
 * VIC — including NSW. The legacy app appears to support an NSW branch but
 * the conditional it ships with is unreachable (three disjoint postcode
 * ranges joined by `&&`), so in practice NSW has always shipped from VIC.
 * We preserve that behaviour for bug-for-bug parity; if product wants the
 * NSW depot wired up properly, that's a follow-up change.
 */
function pickFromPostcode(to: number): string {
  if (to >= 4000 && to <= 4999) return QLD_FROM_POSTCODE
  return VIC_FROM_POSTCODE
}

/** Smallest packaging option whose Weight capacity exceeds the cart's total weight. */
function pickPackage(options: ShippingOption[], totalWeight: number): ShippingOption | null {
  // Options must be sorted ascending by Weight so the first match is the smallest.
  const sorted = [...options].sort((a, b) => parseFloat(a.Weight) - parseFloat(b.Weight))
  return sorted.find((o) => parseFloat(o.Weight) > totalWeight) ?? null
}

export function Cart() {
  const { cart, setQty, removeItem } = useCart()
  const { selection, setSelection } = useShipment()
  const navigate = useNavigate()

  const [collectOptions, setCollectOptions] = useState<CollectOption[]>([])
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [postcode, setPostcode] = useState('')
  const [rateLoading, setRateLoading] = useState(false)
  const [auspostService, setAuspostService] = useState<AuspostService | null>(null)
  const [rateError, setRateError] = useState<string | null>(null)
  const [method, setMethod] = useState<'Pickup' | 'Delivery'>(() => selection?.method ?? 'Pickup')
  const [pickupOptNo, setPickupOptNo] = useState<string>(() =>
    selection?.method === 'Pickup' ? selection.option.OptNo : '',
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [collect, ship] = await Promise.all([getCollectOptions(), getShippingOptions()])
        if (!cancelled) {
          setCollectOptions(collect)
          setShippingOptions(ship)
        }
      } catch (err) {
        if (!cancelled)
          setLoadError(err instanceof Error ? err.message : 'Failed to load shipping options.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const subtotal = useMemo(
    () => cart.items.reduce((sum, item) => sum + item.qty * item.price, 0),
    [cart.items],
  )

  const totalWeight = useMemo(
    () => cart.items.reduce((sum, item) => sum + (item.weight ?? 0) * item.qty, 0),
    [cart.items],
  )

  // Auto-pick the smallest packaging option that can carry the cart's total
  // weight — the user never sees a package picker (matches the legacy app).
  const selectedPackaging = useMemo(
    () => pickPackage(shippingOptions, totalWeight),
    [shippingOptions, totalWeight],
  )

  // Legacy rule: any 'PS' (paints/sprays) item in the cart disables delivery.
  // ProdFilter is persisted on CartItem so this works after a reload too.
  const containsPaint = useMemo(
    () => cart.items.some((i) => i.prodFilter === 'PS'),
    [cart.items],
  )

  const deliveryDisabledReason: 'paint' | 'weight' | null = useMemo(() => {
    if (containsPaint) return 'paint'
    if (shippingOptions.length > 0 && !selectedPackaging) return 'weight'
    return null
  }, [containsPaint, shippingOptions.length, selectedPackaging])

  const deliveryDisabled = deliveryDisabledReason !== null

  // If the cart changes such that Delivery is no longer available, fall back
  // to Pickup automatically so the user isn't stranded on a disabled option.
  useEffect(() => {
    if (deliveryDisabled && method === 'Delivery') setMethod('Pickup')
  }, [deliveryDisabled, method])

  // Latest-request guard: a stale in-flight calc (older weight/postcode)
  // must not overwrite the result of a newer one.
  const rateReqId = useRef(0)

  const recalcRate = useCallback(async () => {
    setRateError(null)
    if (!/^\d{4}$/.test(postcode)) return
    if (!selectedPackaging) {
      setRateError('Cart is too heavy for any available package size.')
      return
    }
    const reqId = ++rateReqId.current
    setRateLoading(true)
    setAuspostService(null)
    try {
      const res = await auspostCalc({
        from_postcode: pickFromPostcode(parseInt(postcode, 10)),
        to_postcode: postcode,
        length: selectedPackaging.Length,
        width: selectedPackaging.Width,
        height: selectedPackaging.Height,
        // Legacy parity: send the cart's actual total weight, not the
        // package's max capacity. Sending the capacity inflates the AusPost
        // quote because it bills by the heavier of dim-weight or actual.
        weight: totalWeight,
      })
      if (reqId !== rateReqId.current) return // superseded by a newer calc
      const services = res.services?.service ?? []
      // Legacy parity: prefer AUS_PARCEL_EXPRESS (legacy DEFAULT_SHIPPING).
      const chosen = services.find((s) => s.code === 'AUS_PARCEL_EXPRESS') ?? services[0] ?? null
      if (chosen) {
        // Legacy parity: shipping shown to the user is auspostRate +
        // packaging surcharge (the cost of the box itself).
        const combined = parseFloat(chosen.price) + parseFloat(selectedPackaging.Price)
        setAuspostService({ ...chosen, price: combined.toFixed(2) })
      } else {
        setRateError('No delivery options available for this postcode.')
      }
    } catch {
      if (reqId !== rateReqId.current) return
      // AusPost rejects unknown / invalid postcodes with a 400 — surface the
      // legacy copy that nudges the user to fix the postcode itself rather
      // than dumping the raw network error.
      setRateError('Unable to fetch delivery price. Please check your postcode.')
    } finally {
      if (reqId === rateReqId.current) setRateLoading(false)
    }
  }, [postcode, selectedPackaging, totalWeight])

  const handlePostcodeBlur = () => {
    void recalcRate()
  }

  // Re-quote when the cart's weight changes after a rate was already shown —
  // legacy re-fetched on every qty change so the delivery cost never goes
  // stale. Only fires once a service exists (i.e. the user has entered a
  // postcode and we're showing a price), so it never quotes prematurely.
  useEffect(() => {
    if (method !== 'Delivery') return
    if (!auspostService && !rateError) return
    void recalcRate()
    // recalcRate is keyed on the same inputs; depend on weight only so this
    // fires on cart changes, not on every recalcRate identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalWeight])

  const proceed = () => {
    setError(null)
    if (cart.items.length === 0) {
      setError('Your cart is empty.')
      return
    }
    if (method === 'Pickup') {
      const opt = collectOptions.find((o) => o.OptNo === pickupOptNo)
      if (!opt) {
        setError('Please choose a pick up location.')
        return
      }
      setSelection({ method: 'Pickup', option: opt })
      navigate('/checkout')
      return
    }
    if (deliveryDisabledReason === 'paint') {
      setError(DELIVERY_DISABLED_PAINT)
      return
    }
    if (deliveryDisabledReason === 'weight' || !selectedPackaging) {
      setError(DELIVERY_DISABLED_WEIGHT)
      return
    }
    if (!auspostService) {
      setError('Enter a valid postcode and we will calculate the rate.')
      return
    }
    setSelection({
      method: 'Delivery',
      packagingOption: selectedPackaging,
      auspostService: {
        code: auspostService.code,
        name: auspostService.name,
        price: auspostService.price,
      },
    })
    navigate('/checkout')
  }

  if (cart.items.length === 0) {
    return (
      <div className="al-cart al-cart--empty text-center">
        <Link to="/" className="al-cart__back m-bx4">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Spare Parts Shop
        </Link>
        <h2>Your cart is empty</h2>
        <p className="m-bx4">Browse the catalog and add the parts you need.</p>
        <Button variant="primary-outline" fill asChild>
          <Link to="/">Browse spare parts</Link>
        </Button>
      </div>
    )
  }

  return (
    <div>
      <Link to="/" className="al-cart__back m-bx4">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Spare Parts Shop
      </Link>
      <h2>Shopping Cart</h2>

      {loadError ? (
        <Alert variant="error" title="Could not load shipping">
          {loadError}
        </Alert>
      ) : null}

      <section className="al-cart__items">
        {cart.items.map((item) => (
          <article key={item.sku} className="al-cart__item">
            <div className="al-cart__img">
              {item.image ? <img src={item.image} alt="" /> : null}
            </div>
            <div className="al-cart__item-body">
              <h3 className="large no-m-t m-bx1">{item.name}</h3>
            </div>
            <p className="al-cart__item-price">${item.price.toFixed(2)}</p>
            <QuantityStepper
              className="al-cart__qty"
              value={item.qty}
              onChange={(next) => setQty(item.sku, next)}
              label={`Quantity for ${item.name}`}
              max={QTY_UPPER_LIMIT}
            />
            <button
              type="button"
              className="al-cart__remove"
              onClick={() => removeItem(item.sku)}
              aria-label={`Remove ${item.name}`}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 38 38"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19 38C8.5 38 0 29.5 0 19S8.5 0 19 0s19 8.5 19 19-8.5 19-19 19zm0-36.5C9.4 1.5 1.5 9.4 1.5 19S9.4 36.5 19 36.5 36.5 28.7 36.5 19 28.7 1.5 19 1.5z" />
                <path d="M24.2 24.9c-.2 0-.4-.1-.5-.2L13.3 14.4c-.3-.3-.3-.8 0-1.1s.8-.3 1.1 0l10.3 10.3c.3.3.3.8 0 1.1-.1.1-.3.2-.5.2z" />
                <path d="M13.9 24.9c-.2 0-.4-.1-.5-.2-.3-.3-.3-.8 0-1.1l10.3-10.3c.3-.3.8-.3 1.1 0s.3.8 0 1.1L14.4 24.7c-.2.1-.4.2-.5.2z" />
              </svg>
            </button>
          </article>
        ))}
      </section>

      <section>
        <h2>Shipping method</h2>
        <RadioGroup
          name="shipment"
          legend=""
          value={method}
          onChange={(v) => setMethod(v as 'Pickup' | 'Delivery')}
          options={[
            {
              // Internal state key — kept as 'Pickup' (one word) so it matches
              // the `ShipmentMethod` union and the `method === 'Pickup'`
              // checks on the Checkout page. `label` is the visible text.
              value: 'Pickup',
              label: 'Pick up',
              // Only render the depot dropdown when Pick up is the active
              // method — keeps the irrelevant input out of the user's way.
              content:
                method === 'Pickup' ? (
                  <div className="dropdown m-bx2">
                    <label className="visuallyhidden" htmlFor="pickup-select">
                      Pick up location
                    </label>
                    <select
                      id="pickup-select"
                      value={pickupOptNo}
                      onChange={(e) => setPickupOptNo(e.target.value)}
                    >
                      <option value="">Select pick up location</option>
                      {collectOptions.map((o) => (
                        <option key={o.OptNo} value={o.OptNo}>
                          {o.Description}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null,
            },
            {
              // `value` is the internal state key — it must stay 'Delivery'
              // so the type ('Pickup' | 'Delivery') and the `method === 'Delivery'`
              // checks downstream (content, totals, proceed) all line up.
              // Use `label` to change what the user sees.
              value: 'Delivery',
              label: 'Delivery (Express)',
              disabled: deliveryDisabled,
              // When the radio is disabled we still want to tell the user
              // *why* — render the legacy reason copy inline (same place the
              // old app put it) rather than swallowing the constraint.
              content: deliveryDisabled ? (
                <p className="al-cart__delivery-reason">
                  {deliveryDisabledReason === 'paint'
                    ? DELIVERY_DISABLED_PAINT
                    : DELIVERY_DISABLED_WEIGHT}
                </p>
              ) : method === 'Delivery' ? (
                <div className="m-bx2">
                  <label className="visuallyhidden" htmlFor="ship-postcode">
                    Enter postcode to calculate shipping
                  </label>
                  <input
                    id="ship-postcode"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter your postcode"
                    className="input-text"
                    value={postcode}
                    onChange={(e) => {
                      setPostcode(e.target.value.replace(/\D/g, '').slice(0, 4))
                      setAuspostService(null)
                    }}
                    onBlur={handlePostcodeBlur}
                  />
                  {rateLoading ? (
                    <p className="al-cart__rate" role="status">
                      Calculating delivery…
                    </p>
                  ) : null}
                  {rateError ? <Alert variant="error">{rateError}</Alert> : null}
                  {!rateLoading && auspostService ? (
                    <p className="al-cart__rate">
                      {auspostService.name}:{' '}
                      <strong>${parseFloat(auspostService.price).toFixed(2)}</strong>
                    </p>
                  ) : null}
                </div>
              ) : null,
            },
          ]}
        />
      </section>

      <section className="al-cart__totals text-right">
        <p>
          Subtotal: <strong>${subtotal.toFixed(2)}</strong>
        </p>
        {method === 'Delivery' && auspostService ? (
          <p>
            Shipping: <strong>${parseFloat(auspostService.price).toFixed(2)}</strong>
          </p>
        ) : null}
        {method === 'Delivery' && auspostService ? (
          <p className="al-cart__total">
            Total: <strong>${(subtotal + parseFloat(auspostService.price)).toFixed(2)}</strong>
          </p>
        ) : (
          <p className="al-cart__total">
            Total: <strong>${subtotal.toFixed(2)}</strong>
          </p>
        )}
        {/* AU GST is 10% baked into the listed price; show the included
            component so the cart matches the legacy line. Parity with the
            old app: computed from item subtotal only, not shipping. */}
        <p className="al-cart__gst">(Incl. ${(subtotal / 11).toFixed(2)} GST)</p>
      </section>

      {error ? <Alert variant="error">{error}</Alert> : null}

      <div className="al-cart__actions m-tx2">
        <Button variant="outline" asChild>
          <Link to="/">Keep shopping</Link>
        </Button>
        <Button variant="primary-outline" fill onClick={proceed}>
          Proceed to checkout
        </Button>
      </div>
    </div>
  )
}
